import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, FolderKanban } from 'lucide-react';
import { SearchableSelect } from '@/components/SearchableSelect';
import api from '@/lib/api';
import { toast } from 'sonner';
import { fmt, ACCOUNT_OPTIONS } from './constants';

export function JournalEntriesTab() {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState({ date: '', account: '', description: '', ref: 'MANUAL', debit: '', credit: '', category: 'Expense', project_id: '', proposal_id: '' });

  const load = useCallback(() => {
    api.get('/accounting/journal-entries').then(r => {
      setEntries(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/proposals').then(r => setProposals((r.data || []).filter(p => p.status !== 'Closed - Lost'))).catch(() => {});
  }, [load]);

  if (loading) return <div className="animate-pulse text-muted-foreground p-4">Loading...</div>;

  const allEntries = entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  // Signed net for a row. Based on category (not raw sign on debit/credit) so
  // that historic entries with corrupt negative-debit values still display the
  // right intent visually.
  // - Expense / Discount / AP outflows → negative (money out)
  // - Income → positive (money in)
  // - Other (Asset/Liability/Equity) → credit - debit (standard double-entry net)
  const signedAmount = (e) => {
    const d = Math.abs(e.debit || 0);
    const c = Math.abs(e.credit || 0);
    const cat = e.category || '';
    if (cat === 'Expense' || cat === 'Discount') return -(d || c);
    if (cat === 'Income') return (c || d);
    return (c - d);
  };

  const totalDebit = allEntries.reduce((s, e) => s + (e.debit || 0), 0);
  const totalCredit = allEntries.reduce((s, e) => s + (e.credit || 0), 0);
  const totalNet = allEntries.reduce((s, e) => s + signedAmount(e), 0);

  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = `${p.project_number} - ${p.name}`; });
  const proposalMap = {};
  proposals.forEach(p => {
    const clientLabel = p.client_name || p.contact_name || '';
    proposalMap[p.id] = clientLabel ? `${p.proposal_number} · ${clientLabel}` : p.proposal_number;
  });

  const sourceBadge = (e) => {
    const source = e?.source;
    // Prefer event_type when it's an accounting calendar entry - more informative than "acct_calendar"
    if ((source === 'acct_calendar' || source === 'acct_calendar_recurring') && e?.event_type) {
      return <Badge variant="outline" className="text-xs bg-amber-500/15 text-amber-400">{source === 'acct_calendar_recurring' ? `${e.event_type} (recurring)` : e.event_type}</Badge>;
    }
    const map = {
      manual: { label: 'Manual', cls: 'bg-secondary text-foreground' },
      invoice_payment: { label: 'Invoice Payment', cls: 'bg-emerald-500/15 text-emerald-400' },
      invoice_discount: { label: 'Invoice Discount', cls: 'bg-amber-500/15 text-amber-400' },
      invoice_discount_reversal: { label: 'Discount Reversed', cls: 'bg-blue-500/15 text-blue-400' },
      dd_invoice: { label: 'DD Invoice', cls: 'bg-primary/15 text-primary' },
      payroll: { label: 'Payroll', cls: 'bg-amber-500/15 text-amber-400' },
      ap_payment: { label: 'AP Payment', cls: 'bg-amber-500/15 text-amber-400' },
      acct_calendar: { label: 'Scheduled Payment', cls: 'bg-amber-500/15 text-amber-400' },
      acct_calendar_recurring: { label: 'Recurring Payment', cls: 'bg-amber-500/15 text-amber-400' },
    };
    const m = map[source] || { label: source || 'Auto', cls: 'bg-muted text-muted-foreground' };
    return <Badge variant="outline" className={`text-xs ${m.cls}`}>{m.label}</Badge>;
  };

  const openForm = (entry = null) => {
    if (entry) {
      setEditEntry(entry);
      setForm({ date: entry.date, account: entry.account, description: entry.description, ref: entry.ref || 'MANUAL', debit: entry.debit || '', credit: entry.credit || '', category: entry.category || 'Expense', project_id: entry.project_id || '', proposal_id: entry.proposal_id || '' });
    } else {
      setEditEntry(null);
      setForm({ date: new Date().toISOString().slice(0, 10), account: '', description: '', ref: 'MANUAL', debit: '', credit: '', category: 'Expense', project_id: '', proposal_id: '' });
    }
    setShowForm(true);
  };

  const saveEntry = async () => {
    const payload = { ...form, debit: parseFloat(form.debit) || 0, credit: parseFloat(form.credit) || 0 };
    if (form.account !== 'Reimbursable Expenses') {
      delete payload.project_id;
      delete payload.proposal_id;
    } else if (payload.project_id && payload.proposal_id) {
      return toast.error('Link to a Project OR a Proposal, not both');
    }
    try {
      if (editEntry) {
        await api.put(`/accounting/journal-entries/${editEntry.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post('/accounting/journal-entries', payload);
        toast.success("Entry created");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save entry");
    }
  };

  const deleteEntry = async (eid) => {
    try {
      await api.delete(`/accounting/journal-entries/${eid}`);
      toast.success("Entry deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-lg" data-testid="journal-entries-heading">Journal Entries</h2>
          <p className="text-xs text-muted-foreground mt-1">Auto-generated from invoices + manual entries for expenses, adjustments, etc.</p>
        </div>
        <Button size="sm" onClick={() => openForm()} data-testid="add-journal-entry-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Entry
        </Button>
      </div>
      <Card className="border-border bg-card shadow-none">
        <CardContent className="p-0">
          <div className="max-h-[65vh] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider bg-card">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider bg-card">Ref</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider bg-card">Account</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider bg-card">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right bg-card">Debit</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right bg-card">Credit</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right bg-card">Net</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center bg-card">Source</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider w-16 bg-card"></TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
              {allEntries.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground text-xs py-6">No journal entries yet.</TableCell></TableRow>
              ) : allEntries.slice(0, 60).map(e => {
                const net = signedAmount(e);
                const netClass = net > 0 ? 'text-emerald-400' : net < 0 ? 'text-destructive' : 'text-muted-foreground';
                const netStr = net === 0 ? '' : `${net > 0 ? '+' : '-'}${fmt(Math.abs(net))}`;
                return (
                <TableRow key={e.id} className={`border-border ${e.source === 'manual' ? 'bg-secondary/10' : ''}`}>
                  <TableCell className="text-xs">{e.date}</TableCell>
                  <TableCell className="text-xs font-mono">{e.ref}</TableCell>
                  <TableCell className="text-xs font-medium">
                    {e.account}
                    {e.project_id && e.is_company_reimbursable && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <FolderKanban className="h-2.5 w-2.5 text-primary" />
                        <span className="text-xs text-primary truncate max-w-[150px]">{projectMap[e.project_id] || 'Project'}</span>
                      </div>
                    )}
                    {e.proposal_id && e.is_company_reimbursable && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <FolderKanban className="h-2.5 w-2.5 text-amber-400" />
                        <span className="text-xs text-amber-400 truncate max-w-[150px]">{proposalMap[e.proposal_id] || 'Proposal'}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate">{e.description}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{e.debit ? fmt(e.debit) : ''}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{e.credit ? fmt(e.credit) : ''}</TableCell>
                  <TableCell className={`text-xs text-right tabular-nums font-medium ${netClass}`} data-testid={`je-net-${e.id}`}>{netStr}</TableCell>
                  <TableCell className="text-center">{sourceBadge(e)}</TableCell>
                  <TableCell className="text-right">
                    {e.source === 'manual' && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openForm(e)} data-testid={`edit-je-${e.id}`}><Pencil className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => deleteEntry(e.id)} data-testid={`delete-je-${e.id}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );})}
              {/* Totals Row */}
              <TableRow className="border-border bg-secondary/20 font-bold">
                <TableCell colSpan={4} className="text-xs text-right uppercase tracking-wider">Totals</TableCell>
                <TableCell className="text-xs text-right tabular-nums" data-testid="je-total-debit">{fmt(totalDebit)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums" data-testid="je-total-credit">{fmt(totalCredit)}</TableCell>
                <TableCell
                  className={`text-xs text-right tabular-nums ${totalNet > 0 ? 'text-emerald-400' : totalNet < 0 ? 'text-destructive' : ''}`}
                  data-testid="je-total-net"
                  title="Net = sum of signed amounts. Expenses/discounts negative, income positive."
                >
                  {totalNet === 0 ? fmt(0) : `${totalNet > 0 ? '+' : '-'}${fmt(Math.abs(totalNet))}`}
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editEntry ? 'Edit' : 'New'} Journal Entry</DialogTitle>
            <DialogDescription>Record a manual accounting entry with debit or credit amount.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="h-8 text-xs bg-secondary/30 border-border" data-testid="je-form-date" />
              </div>
              <div>
                <Label className="text-xs">Reference</Label>
                <Input value={form.ref} onChange={e => setForm(f => ({ ...f, ref: e.target.value }))} className="h-8 text-xs bg-secondary/30 border-border" data-testid="je-form-ref" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Account</Label>
              <SearchableSelect
                value={form.account}
                onValueChange={v => setForm(f => ({ ...f, account: v, project_id: v !== 'Reimbursable Expenses' ? '' : f.project_id, proposal_id: v !== 'Reimbursable Expenses' ? '' : f.proposal_id }))}
                options={ACCOUNT_OPTIONS.map(a => ({ value: a, label: a }))}
                placeholder="Select account..."
                searchPlaceholder="Search accounts..."
                data-testid="je-form-account"
              />
            </div>
            {/* Show project/proposal selectors when account is Reimbursable Expenses */}
            {form.account === 'Reimbursable Expenses' && (
              <>
                <div>
                  <Label className="text-xs">Link to Project</Label>
                  <SearchableSelect
                    value={form.project_id || ''}
                    onValueChange={v => setForm(f => ({ ...f, project_id: v, proposal_id: v ? '' : f.proposal_id }))}
                    options={[{ value: '', label: 'No project' }, ...projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))]}
                    placeholder="Select project..."
                    searchPlaceholder="Search projects..."
                    data-testid="je-form-project"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Appears in Project P&L and can be added to invoices as a reimbursable.</p>
                </div>
                {!form.project_id && (
                  <div>
                    <Label className="text-xs">Or link to Proposal (Due Diligence)</Label>
                    <SearchableSelect
                      value={form.proposal_id || ''}
                      onValueChange={v => setForm(f => ({ ...f, proposal_id: v, project_id: v ? '' : f.project_id }))}
                      options={[{ value: '', label: 'No proposal' }, ...proposals.map(p => {
                        const clientLabel = p.client_name || p.contact_name || '';
                        return { value: p.id, label: clientLabel ? `${p.proposal_number} · ${clientLabel}` : p.proposal_number };
                      })]}
                      placeholder="Select proposal..."
                      searchPlaceholder="Search proposals..."
                      data-testid="je-form-proposal"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Available to attach to the proposal&apos;s Due Diligence invoice.</p>
                  </div>
                )}
              </>
            )}
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-8 text-xs bg-secondary/30 border-border" data-testid="je-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Debit ($)</Label>
                <Input type="number" step="0.01" min="0" value={form.debit} onChange={e => setForm(f => ({ ...f, debit: e.target.value, credit: e.target.value ? '' : f.credit }))} className="h-8 text-xs bg-secondary/30 border-border" data-testid="je-form-debit" />
              </div>
              <div>
                <Label className="text-xs">Credit ($)</Label>
                <Input type="number" step="0.01" min="0" value={form.credit} onChange={e => setForm(f => ({ ...f, credit: e.target.value, debit: e.target.value ? '' : f.debit }))} className="h-8 text-xs bg-secondary/30 border-border" data-testid="je-form-credit" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={saveEntry} data-testid="je-form-save-btn">{editEntry ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
